import { FC, useState } from 'react';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Chip, { ChipProps } from '@mui/material/Chip';
import type { SxProps } from '@mui/system';
import { useTranslation, TFunction } from 'react-i18next';
import { BiAddToQueue } from 'react-icons/bi';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha } from '@mui/material/styles';

import type { IFolder } from 'typings/document';
import UploadFilesModal from 'components/modals/UploadFilesModal';
import { boldenLastWord, humanFileSize } from 'utils/helpers';

interface INodeProps {
  folder: IFolder;
  refIndex: number;
  reference: string;
}

const buttonExtraStyles: SxProps = {
  width: [1, undefined, 0.5],
  py: 1.5,
  mb: 0.5,
  fontWeight: 'normal',
  justifyContent: 'space-between',
  '& .MuiButton-endIcon > svg': {
    fontSize: 'h5.fontSize',
    opacity: 0.7,
  },
};

const getChipProps = (
  folder: IFolder,
  translate: TFunction<'translation', undefined>
): ChipProps => {
  const { documents, isRequired } = folder;
  const isCompleted = !!(isRequired && documents?.length);

  if (isCompleted) {
    return {
      label: translate('Common.CompletedStep'),
      variant: 'filled',
      color: 'success',
      icon: <CheckCircleIcon />,
      sx: {
        flexDirection: 'row-reverse',
        pr: 0.75,
        backgroundColor: (theme) => alpha(theme.palette.success.main, 0.15),
        color: 'success.main',
        '& svg': {
          fontSize: 'min(33.75px, 2rem)',
        },
      },
    };
  }

  if (isRequired) {
    return {
      label: translate('Common.Required'),
      variant: 'outlined',
      color: 'primary',
    };
  }

  return {
    label: translate('Common.Optional'),
    variant: 'outlined',
    sx: { color: 'common.grey', borderColor: 'common.grey' },
  };
};

const Folder: FC<INodeProps> = (props) => {
  const { folder, reference } = props;
  const { t: translate, i18n } = useTranslation();

  const [isFilesModalOpen, setIsFilesModalOpen] = useState(false);

  return (
    <>
      <Paper
        component={Stack}
        elevation={2}
        width={1}
        p={2}
        borderRadius="0.5rem !important"
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          pb={0.75}
        >
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="primary.main"
          >
            {folder.name}
          </Typography>
          {typeof folder.isRequired === 'boolean' && (
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            <Chip {...getChipProps(folder, translate)} />
          )}
        </Stack>

        <Typography variant="body2" pb={2}>
          {folder?.fileExtensions?.length
            ? `.${folder.fileExtensions.join(', .')}`.toUpperCase()
            : translate('Common.AllTypes')}
        </Typography>

        {folder?.documents?.map(({ name, extension, size, createdAt }) => {
          const createdAtDate = new Date(createdAt);
          const createdAtDateFormatted = createdAtDate.toLocaleDateString(
            i18n.language
          );
          const createdAtTimeFormatted = createdAtDate.toLocaleTimeString(
            i18n.language
          );

          return (
            <Stack key={name} pb={1}>
              <Stack
                direction="row"
                alignItems="center"
                bgcolor="background.default"
                py={1}
                px={1.75}
                borderRadius="0.5rem"
              >
                <Tooltip disableFocusListener placement="top" title={name}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    pb={0.5}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    maxWidth={1}
                    color="primary.main"
                  >
                    {name}
                  </Typography>
                </Tooltip>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  ml="auto"
                >
                  <Typography
                    variant="caption"
                    color="common.grey"
                    fontWeight={600}
                    minWidth="6rem"
                    pr={1}
                    textAlign="right"
                    sx={{ textTransform: 'uppercase' }}
                  >
                    {extension} - {humanFileSize(size)}
                  </Typography>

                  <Tooltip
                    disableFocusListener
                    placement="top"
                    title={createdAtTimeFormatted}
                  >
                    <Typography
                      variant="caption"
                      color="primary.main"
                      minWidth="5rem"
                      ml="auto"
                      textAlign="right"
                      fontWeight={600}
                    >
                      {createdAtDateFormatted}
                    </Typography>
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>
          );
        })}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
        >
          <Button
            endIcon={<BiAddToQueue />}
            onClick={() => setIsFilesModalOpen(true)}
            sx={buttonExtraStyles}
          >
            <span>{boldenLastWord(translate('Common.AddDocuments'))}</span>
          </Button>
        </Stack>
      </Paper>

      <UploadFilesModal
        open={isFilesModalOpen}
        setOpen={setIsFilesModalOpen}
        folder={folder}
        reference={reference}
      />
    </>
  );
};

export default Folder;
